import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Download, Heart, Sun, Moon } from "lucide-react";
import { api, resolveMediaUrl } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import useBrand from "@/hooks/useBrand";
import { useTheme } from "@/contexts/ThemeContext";

const NAV = [
    { id: "o-projekcie", label: "O projekcie" },
    { id: "galeria", label: "Galeria" },
    { id: "mapa", label: "Interaktywna mapa" },
    { id: "pobierz", label: "Pobierz" },
    { id: "archiwum", label: "Archiwum" },
    { id: "zespol", label: "Zespół" },
    { id: "faq", label: "FAQ" },
    { id: "kontakt", label: "Społeczność" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [supportHref, setSupportHref] = useState("");
    const location = useLocation();
    const navigate = useNavigate();
    const { t, lp, lang } = useLang();
    // Shared brand hook: fetches mod-info logo_url and applies favicon dynamically
    const { logoUrl, logoSrc } = useBrand();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/social");
                const support = (data || []).filter((l) => l.category === "support");
                if (support.length > 0) {
                    const u = support[0].url || "";
                    setSupportHref(/^https?:\/\//.test(u) ? u : `https://${u}`);
                }
            } catch {
                /* ignore */
            }
        })();
    }, []);

    const isHome = location.pathname === "/" || location.pathname === "/en";

    // Set the browser favicon to the uploaded logo (stored in DB). Removes any
    // competing <link rel="icon"> tags (e.g. with `sizes` attribute) so browsers
    // don't keep showing the static default. Restores /favicon.ico when no logo.
    // NOTE: This is now handled by the shared useBrand() hook above.
    // (kept comment for context)

    const goSection = (id) => {
        setOpen(false);
        if (!isHome) {
            navigate(lang === "en" ? `/en#${id}` : `/#${id}`);
            return;
        }
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="site-header"
            style={{ top: "var(--announcement-h, 0px)" }}
            className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? "glass" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 xl:px-12 h-20 flex items-center justify-between gap-3 sm:gap-6 w-full overflow-hidden">
                <Link
                    to={lp("/")}
                    data-testid="header-logo"
                    className="flex items-center gap-3 group min-w-0 flex-1 xl:flex-initial xl:max-w-[140px] 2xl:max-w-[240px] overflow-hidden"
                >
                    {logoUrl ? (
                        <span className="inline-flex items-center justify-center h-9 sm:h-10 md:h-11 xl:h-8 2xl:h-11 max-w-[40vw] sm:max-w-[160px] md:max-w-[200px] xl:max-w-[100px] 2xl:max-w-[200px] overflow-hidden shrink-0">
                            <img
                                src={resolveMediaUrl(logoUrl)}
                                alt="Project Ruda Śląska"
                                className="max-h-full max-w-full w-auto h-auto object-contain block"
                                loading="eager"
                            />
                        </span>
                    ) : (
                        <span className="w-9 h-9 flex items-center justify-center rounded-sm border border-[color:var(--border-strong)] text-[color:var(--primary)] font-display font-bold shrink-0">
                            PR
                        </span>
                    )}
                    <span className={`${logoUrl ? "hidden" : "hidden sm:flex xl:hidden 2xl:flex"} flex-col leading-tight whitespace-nowrap min-w-0 overflow-hidden`}>
                        <span className="font-display font-semibold uppercase tracking-wider text-sm 2xl:text-base text-white truncate">
                            Project Ruda Śląska
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-secondary)] truncate">
                            {t("ETS2 · Mapa 1:1")}
                        </span>
                    </span>
                </Link>

                <nav className="hidden xl:flex items-center gap-2 2xl:gap-6 shrink min-w-0">
                    {NAV.map((n) => (
                        <button
                            key={n.id}
                            data-testid={`nav-${n.id}`}
                            onClick={() => goSection(n.id)}
                            className="text-xs uppercase tracking-[0.18em] text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] transition-colors whitespace-nowrap"
                        >
                            {t(n.label)}
                        </button>
                    ))}
                    <Link
                        to={lp("/zglos-blad")}
                        data-testid="nav-report-bug"
                        className="text-xs uppercase tracking-[0.18em] text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] transition-colors whitespace-nowrap"
                    >
                        {t("Zgłoś błąd")}
                    </Link>
                </nav>

                <div className="flex items-center gap-2 xl:gap-3 xl:ml-3 2xl:ml-6 xl:pl-3 2xl:pl-6 xl:border-l xl:border-white/10 shrink-0">
                    <ThemeToggle />
                    {supportHref ? (
                        <a
                            href={supportHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-support-cta"
                            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider font-semibold rounded-md border border-[color:var(--primary)]/40 text-[color:var(--primary)] hover:bg-[color:var(--primary)] hover:text-[color:var(--bg-base)] transition-colors"
                        >
                            <Heart size={14} strokeWidth={2} /> {t("Wsparcie")}
                        </a>
                    ) : (
                        <button
                            onClick={() => goSection("kontakt")}
                            data-testid="header-support-cta"
                            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider font-semibold rounded-md border border-[color:var(--primary)]/40 text-[color:var(--primary)] hover:bg-[color:var(--primary)] hover:text-[color:var(--bg-base)] transition-colors"
                        >
                            <Heart size={14} strokeWidth={2} /> {t("Wsparcie")}
                        </button>
                    )}
                    <button
                        data-testid="header-download-cta"
                        onClick={() => goSection("pobierz")}
                        className="hidden md:inline-flex btn-primary !px-5 !py-2.5 !text-xs"
                    >
                        <Download size={14} strokeWidth={2} /> {t("Pobierz")}
                    </button>
                    <button
                        data-testid="header-menu-toggle"
                        className="xl:hidden text-white p-2"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div data-testid="mobile-menu" className="xl:hidden glass border-t border-white/5">
                    <div className="px-4 sm:px-6 py-6 flex flex-col gap-4">
                        {NAV.map((n) => (
                            <button
                                key={n.id}
                                data-testid={`mobile-nav-${n.id}`}
                                onClick={() => goSection(n.id)}
                                className="text-left text-base uppercase tracking-[0.2em] text-[color:var(--text-secondary)] hover:text-[color:var(--primary)]"
                            >
                                {t(n.label)}
                            </button>
                        ))}
                        <Link
                            to={lp("/zglos-blad")}
                            data-testid="mobile-nav-report-bug"
                            onClick={() => setOpen(false)}
                            className="text-left text-base uppercase tracking-[0.2em] text-[color:var(--text-secondary)] hover:text-[color:var(--primary)]"
                        >
                            {t("Zgłoś błąd")}
                        </Link>
                        {supportHref ? (
                            <a
                                href={supportHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setOpen(false)}
                                data-testid="mobile-support-cta"
                                className="inline-flex items-center gap-2 text-left text-base uppercase tracking-[0.2em] text-[color:var(--primary)]"
                            >
                                <Heart size={16} /> {t("Wsparcie")}
                            </a>
                        ) : (
                            <button
                                onClick={() => goSection("kontakt")}
                                data-testid="mobile-support-cta"
                                className="inline-flex items-center gap-2 text-left text-base uppercase tracking-[0.2em] text-[color:var(--primary)]"
                            >
                                <Heart size={16} /> {t("Wsparcie")}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}

/** Header sub-component: dark/light theme toggle button.
 *  Uses the CSS `data-theme` attribute already wired via ThemeProvider. */
function ThemeToggle() {
    const { theme, toggle } = useTheme();
    const isLight = theme === "light";
    return (
        <button
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label={isLight ? "Przełącz na ciemny motyw" : "Przełącz na jasny motyw"}
            title={isLight ? "Ciemny motyw" : "Jasny motyw"}
            className="w-9 h-9 rounded-md flex items-center justify-center text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] hover:bg-[color:var(--primary)]/10 transition-colors"
        >
            {isLight ? <Moon size={16} strokeWidth={1.9} /> : <Sun size={16} strokeWidth={1.9} />}
        </button>
    );
}
